import { Component, Element, Event, EventEmitter, h, Host, Prop } from "@stencil/core";

@Component({
    tag: "ux-tag",
    styleUrl: "tag.scss",
    shadow: true
})
export class Tag {
    /**
     * Primary element reference
     */
    @Element() domNode: HTMLUxTagElement;

    /**
     * Define reference to parent container
     * @deprecated
     * @default undefined
     */
    @Prop({ reflect: true }) readonly reference: string;

    /**
     * Control visibility of closing x icon & enable uxRemove Event
     * @default false
     */
    @Prop({ reflect: true }) readonly removable: boolean = false;

    /** Color variant of the tag */
    @Prop() readonly variant: "default" | "secondary" = "default";

    /**
     * If set to true, the tag is styled with the interactive variant.
     * This prop will be removed as soon as the interactive style is considered as default.
     */
    @Prop() readonly interactive: boolean = false;

    /**
     * Event emitted when tag button pressed if removable attribute present
     */
    @Event({
        eventName: "uxRemove",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    remove: EventEmitter;

    /**
     * Remove Tag DOM Node
     * @deprecated Remove logic will be removed with next major. Use the uxRemove Event instead
     * @migrationHint Use uxRemove event and remove dom node by yourself
     * @private
     */
    private removeTag = (): void => {
        if (!this.removable) {
            return;
        }
        if (this.remove.emit(this.reference).defaultPrevented) {
            return;
        }
        this.domNode.remove();
    };

    private handleClick = (event: Event) => {
        if (this.removable) {
            event.preventDefault();
            this.removeTag();
        }
    };

    private handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
            case "Enter":
            case " ":
                this.domNode.dispatchEvent(new UIEvent("click"));
                break;
            default:
                break;
        }
    };

    render() {
        const { removable, interactive, variant } = this;
        const isButton = removable || interactive;
        return (
            <Host
                class={{
                    [`ux-tag--removable`]: removable,
                    [`ux-tag--interactive`]: interactive,
                    [`ux-tag--${variant}`]: variant !== "default"
                }}
                onClick={removable ? this.handleClick : undefined}
                onKeyDown={isButton ? this.handleKeyDown : undefined}
                role={isButton ? "button" : undefined}
                tabindex={isButton ? 0 : undefined}
            >
                <span class="ux-tag__label">
                    <slot />
                </span>
                {removable && <ux-icon name="cross" aria-hidden="true" weight="normal" />}
            </Host>
        );
    }
}
